// javascript
import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import GavelIcon from '@mui/icons-material/Gavel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import WarningIcon from '@mui/icons-material/Warning'; // <-- NUEVO: Para el modal de alerta
import cambioEstadoService from '../services/cambioEstadoService';
import EventoService from '../services/eventoService';
import NotificacionService from '../services/notificacionService';
import BotonWhatsapp from '../components/BotonWhatsapp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ModificarEventoModal from '../components/ModificarEventoModal';
import TarjetaCitacion from '../components/recepcion/CitacionEstudio';

// Función auxiliar para obtener la fecha actual en formato YYYY-MM-DD
const obtenerFechaActual = () => {
    const hoy = new Date();
    return hoy.toISOString().split('T')[0];
};

function GestorEstadoPericiaAsignada({ tramite, onEstadoCambiado }) {
    const [cargando, setCargando] = useState(false);
    const [historialEstados, setHistorialEstados] = useState([]);
    const [cargandoHistorial, setCargandoHistorial] = useState(true);
    const [infoPericia, setInfoPericia] = useState(null);
    const [eventoPericia, setEventoPericia] = useState(null); // Para guardar el evento y su ID

    // Estados para los formularios
    const [mostrarFormularioResultado, setMostrarFormularioResultado] = useState(false);
    const [mostrarFormularioReprogramar, setMostrarFormularioReprogramar] = useState(false);
    const [mostrarModalConfirmacionNoAsistio, setMostrarModalConfirmacionNoAsistio] = useState(false); // <-- NUEVO
    const [mostrarModificar, setMostrarModificar] = useState(false);

    // Datos del formulario de resultado
    const [datosResultado, setDatosResultado] = useState({
        fechaRealizacion: obtenerFechaActual(),
        observaciones: ''
    });

    // Datos del formulario de reprogramación
    const [datosReprogramar, setDatosReprogramar] = useState({
        fecha: obtenerFechaActual(),
        hora: '',
        lugar: '',
        motivo: ''
    });

    useEffect(() => {
        cargarHistorialYEvento();
    }, [tramite.idTramite, onEstadoCambiado]);

    const cargarHistorialYEvento = async () => {
        setCargandoHistorial(true);
        try {
            // 1. Cargar Historial
            const historial = await cambioEstadoService.getHistorialEstados(tramite.idTramite);
            setHistorialEstados(historial);

            // 2. Buscar info de la pericia asignada (último comentario)
            const cambioPericiaAsignada = historial
                .filter(c => c.comentario && c.comentario.includes('Pericia Médica Judicial Asignada'))
                .pop();

            if (cambioPericiaAsignada && cambioPericiaAsignada.comentario) {
                parsearInfoPericia(cambioPericiaAsignada.comentario);
            }

            // 3. Cargar Evento del calendario
            const response = await EventoService.getEventosPorTramite(tramite.idTramite);
            const eventos = response.data;

            const ultimoEventoPericia = eventos
                .filter(e => e.tipoEvento === 'Pericia Médica Judicial')
                .pop();

            if (ultimoEventoPericia) {
                setEventoPericia(ultimoEventoPericia);
                // Pre-llenar formulario de reprogramación con datos actuales
                setDatosReprogramar(prev => ({
                    ...prev,
                    lugar: extraerLugar(ultimoEventoPericia.descripcion)
                }));
            }

        } catch (error) {
            console.error("Error al cargar historial y evento:", error);
            toast.error("No se pudo cargar la información de la pericia.");
        } finally {
            setCargandoHistorial(false);
        }
    };

    const parsearInfoPericia = (comentario) => {
        const info = {
            fecha: null,
            hora: null,
            lugar: null,
            observaciones: null
        };

        const fechaMatch = comentario.match(/Fecha: (.+?)(?:\n|$)/);
        if (fechaMatch) info.fecha = fechaMatch[1].trim();

        const horaMatch = comentario.match(/Hora: (.+?)(?:\n|$)/);
        if (horaMatch) info.hora = horaMatch[1].trim();

        const lugarMatch = comentario.match(/Sede\/Lugar: (.+?)(?:\n|$)/);
        if (lugarMatch) info.lugar = lugarMatch[1].trim();

        const obsMatch = comentario.match(/Observaciones: (.+?)(?:\n|$)/);
        if (obsMatch && !obsMatch[1].includes('Sin observaciones')) info.observaciones = obsMatch[1].trim();

        setInfoPericia(info);

        // Pre-llenar la fecha de realización con la fecha de la pericia
        const fechaISO = parsearFechaDesdeTexto(info.fecha);
        if(fechaISO) {
            setDatosResultado(prev => ({...prev, fechaRealizacion: fechaISO.toISOString().split('T')[0]}));
        }
    };

    const parsearFechaDesdeTexto = (fechaTexto) => {
        if (!fechaTexto) return null;
        try {
            const meses = {
                'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3,
                'mayo': 4, 'junio': 5, 'julio': 6, 'agosto': 7,
                'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
            };
            const partes = fechaTexto.toLowerCase().split(' ');
            if (partes.length >= 5) { // Ej: "viernes, 15 de noviembre de 2025"
                const dia = parseInt(partes[1]);
                const mes = meses[partes[3]];
                const año = parseInt(partes[5]);
                if (!isNaN(dia) && mes !== undefined && !isNaN(año)) {
                    return new Date(año, mes, dia);
                }
            }
        } catch (e) { console.error("Error parseando fecha:", e); }
        return null;
    };

    const extraerLugar = (descripcion) => {
        if (!descripcion) return '';
        const lugarMatch = descripcion.match(/Sede\/Lugar: (.+?)(?:\n|$)/);
        return lugarMatch ? lugarMatch[1].trim() : '';
    };

    const calcularHoraFin = (horaInicio, duracionMinutos = 60) => {
        if (!horaInicio) return '';
        const [horas, minutos] = horaInicio.split(':').map(Number);
        const fechaInicio = new Date();
        fechaInicio.setHours(horas, minutos, 0, 0);
        fechaInicio.setMinutes(fechaInicio.getMinutes() + duracionMinutos);
        return fechaInicio.toTimeString().slice(0, 5);
    };

    // --- ACCIONES ---

    const handleRegistrarPericiaRealizada = async () => {
        if (!datosResultado.fechaRealizacion) {
            toast.error("Debe indicar la fecha en que se realizó la pericia.");
            return;
        }

        setCargando(true);
        try {
            const comentario = `Pericia Médica Realizada\nFecha de realización: ${formatearFecha(datosResultado.fechaRealizacion)}\nObservaciones: ${datosResultado.observaciones || 'Sin observaciones'}`;

            // Cambiar a estado "Espera Dictamen Tribunal" (ID 18) [cite: 92]
            const cambioEstadoData = {
                idEstado: 18,
                comentario: comentario
            };

            await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);

            toast.success("Pericia registrada como realizada. El trámite está en espera de dictamen.");

            setMostrarFormularioResultado(false);
            setDatosResultado({ fechaRealizacion: '', observaciones: '' });

            if (onEstadoCambiado) await onEstadoCambiado();

        } catch (error) {
            console.error("Error al registrar pericia realizada:", error);
            toast.error("Error al registrar la pericia: " + (error.response?.data || error.message));
        } finally {
            setCargando(false);
        }
    };

    const handleReprogramarPericia = async () => {
        if (!datosReprogramar.fecha || !datosReprogramar.hora || !datosReprogramar.lugar.trim() || !datosReprogramar.motivo.trim()) {
            toast.error("Debe completar Fecha, Hora, Lugar y Motivo de la reprogramación.");
            return;
        }
        if (!eventoPericia || !eventoPericia.idEvento) {
            toast.error("Error: No se encontró el evento original para reprogramar.");
            return;
        }

        setCargando(true);
        try {
            // 1. Eliminar el evento anterior
            await EventoService.eliminarEvento(eventoPericia.idEvento);

            // 2. Crear el nuevo evento
            const nuevoEventoData = {
                titulo: `Pericia Médica Judicial (Reprogramada) - ${tramite.cliente?.nombre || ''} ${tramite.cliente?.apellido || ''}`,
                descripcion: `Sede/Lugar: ${datosReprogramar.lugar}\nObservaciones: ${datosReprogramar.observaciones || 'Sin observaciones'}`,
                fecha: datosReprogramar.fecha,
                horaInicio: datosReprogramar.hora,
                horaFin: calcularHoraFin(datosReprogramar.hora),
                todoElDia: false,
                tipoEvento: 'Pericia Médica Judicial',
                idTramite: tramite.idTramite,
                visibilidad: 'POR_AREA',
                canalesNotificacion: {
                    whatsapp: false,
                    email: false,
                    inapp: true
                }
            };
            await EventoService.crearEvento(nuevoEventoData);

            // 3. Registrar el cambio en el historial (manteniendo el estado 17)
            const comentario = `REPROGRAMACIÓN Pericia Médica\nMotivo: ${datosReprogramar.motivo}\nNueva Fecha: ${formatearFecha(datosReprogramar.fecha)}\nNueva Hora: ${datosReprogramar.hora}\nNuevo Lugar: ${datosReprogramar.lugar}`;

            const cambioEstadoData = {
                idEstado: 17, // Mantener en estado 17
                comentario: comentario
            };
            await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);

            toast.success("Pericia reprogramada y evento actualizado.");

            setMostrarFormularioReprogramar(false);
            setDatosReprogramar({ fecha: obtenerFechaActual(), hora: '', lugar: infoPericia?.lugar || '', motivo: '' });

            if (onEstadoCambiado) await onEstadoCambiado();

        } catch (error) {
            console.error("Error al reprogramar pericia:", error);
            toast.error("Error al reprogramar la pericia: " + (error.response?.data || error.message));
        } finally {
            setCargando(false);
        }
    };

    // <-- NUEVA FUNCIÓN (Lógica extraída de handleNoAsistio)
    const ejecutarRegistroNoAsistio = async () => {
        setCargando(true);
        try {
            const comentario = `⚠️ INASISTENCIA - El cliente no asistió a la pericia programada para el ${infoPericia?.fecha} a las ${infoPericia?.hora}.`;

            const cambioEstadoData = {
                idEstado: 17, // Mantener en estado 17
                comentario: comentario
            };

            await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);

            toast.warning("Inasistencia registrada. Por favor, reprograme la pericia.");

            setMostrarModalConfirmacionNoAsistio(false); // <-- NUEVO: Cerrar modal de confirmación
            setMostrarFormularioReprogramar(true); // Abrir modal para reprogramar

            if (onEstadoCambiado) await onEstadoCambiado();

        } catch (error)
        {
            console.error("Error al registrar inasistencia:", error);
            toast.error("Error al registrar la inasistencia: " + (error.response?.data || error.message));
        } finally {
            setCargando(false);
        }
    };

    const formatearFecha = (fechaString) => {
        if (!fechaString) return '';
        const fecha = new Date(fechaString);
        if (isNaN(fecha.getTime())) return '';
        const fechaLocal = new Date(fecha.valueOf() + fecha.getTimezoneOffset() * 60000);
        return fechaLocal.toLocaleDateString('es-ES', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    // <-- NUEVO: Renderiza el modal de confirmación
    const renderModalConfirmacionNoAsistio = () => (
        <div className="mt-4">
            <div className="card shadow-sm border-danger border-2 rounded-4 p-4 text-center">
                <h5 className="fw-semibold text-danger mb-3">
                    <WarningIcon className="me-2" />
                    Confirmar Inasistencia
                </h5>
                <p className="text-muted">
                    ¿Está seguro que desea registrar la inasistencia a la pericia programada
                    para el <strong>{infoPericia?.fecha}</strong> a las <strong>{infoPericia?.hora}</strong>?
                </p>
                <p className="text-muted small">
                    Esta acción registrará la inasistencia en el historial y le pedirá que reprograme.
                </p>
                <div className="d-flex justify-content-center gap-3 mt-3">
                    <button
                        className="btn btn-danger d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={ejecutarRegistroNoAsistio} // <-- Llama a la lógica async
                        disabled={cargando}
                    >
                        <EventBusyIcon fontSize="small" />
                        {cargando ? 'Registrando...' : 'Sí, Confirmar Inasistencia'}
                    </button>
                    <button
                        className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={() => setMostrarModalConfirmacionNoAsistio(false)} // <-- Solo cierra el modal
                        disabled={cargando}
                    >
                        <CancelIcon fontSize="small" />
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );

    // --- RENDERIZADO ---

    if (cargandoHistorial) {
        return (
            <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
                <div className="alert alert-info">Cargando información de la pericia...</div>
            </div>
        );
    }

    const handleGuardarModificacionEvento = async (idEvento, eventoActualizado) => {
        try {
            await EventoService.modificarEvento(idEvento, eventoActualizado);
            setMostrarModificar(false);

            const response = await EventoService.getEventosPorTramite(tramite.idTramite);
            const eventos = response.data;
            const ultimoEventoPericia = eventos
                .filter(e => e.tipoEvento === 'Pericia Médica Judicial')
                .pop();
            if (ultimoEventoPericia) {
                setEventoPericia(ultimoEventoPericia);
                setDatosReprogramar(prev => ({
                    ...prev,
                    lugar: extraerLugar(ultimoEventoPericia.descripcion)
                }));
            }
            toast.success('Evento de pericia actualizado correctamente');
        } catch (error) {
            console.error('Error al recargar evento:', error);
            toast.error('No se pudo actualizar el evento');
        }
    };

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-success mb-0">
                    <EventAvailableIcon className="me-2" />
                    Pericia Médica Asignada
                </h4>
            </div>

            {/* Tarjeta de detalle de la pericia */}
            <div className="card bg-light border-0 rounded-3 p-4 mb-3">
                <h5 className="fw-semibold text-secondary mb-3">
                    Detalles de la Pericia Judicial
                </h5>
                {infoPericia ? (
                    <div className="row g-3">
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <CalendarMonthIcon fontSize="small" className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Fecha</small>
                                    <strong className="text-dark">{infoPericia.fecha}</strong>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <AccessTimeIcon fontSize="small" className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Hora</small>
                                    <strong className="text-dark">{infoPericia.hora}</strong>
                                </div>
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="d-flex align-items-center">
                                <PlaceIcon fontSize="small" className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Sede / Lugar</small>
                                    <strong className="text-dark">{infoPericia.lugar}</strong>
                                </div>
                            </div>
                        </div>
                        {infoPericia.observaciones && (
                            <div className="col-12 mt-3">
                                <small className="text-muted d-block">Observaciones:</small>
                                <p className="text-dark mb-0">{infoPericia.observaciones}</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="alert alert-warning">No se pudo cargar la información de la pericia.</div>
                )}

                <div className="mt-3 d-flex gap-2">
                    <BotonWhatsapp
                        idEvento={eventoPericia?.idEvento}
                        numeroTelefono={tramite.cliente?.telefonoPropio || tramite.cliente?.telefono}
                        nombreCliente={`${tramite.cliente?.nombre || ''} ${tramite.cliente?.apellido || ''}`.trim()}
                        contactosAdicionales={tramite.cliente?.contactos}
                        fechaTurno={
                            eventoPericia?.fecha ||
                            (() => {
                                // Si no hay evento, intentamos parsear el texto "miércoles..." a fecha válida
                                const fechaObj = parsearFechaDesdeTexto(infoPericia?.fecha);
                                if (fechaObj) {
                                    // CAMBIO: Usamos slashes (/) en lugar de guiones (-)
                                    // "YYYY-MM-DD" se interpreta como UTC (causando desfase al día 20).
                                    // "YYYY/MM/DD" se interpreta como Local Time (mantiene el día 21).
                                    return `${fechaObj.getFullYear()}/${String(fechaObj.getMonth() + 1).padStart(2, '0')}/${String(fechaObj.getDate()).padStart(2, '0')}`;
                                }
                                return null;
                            })()
                        }

                        horaTurno={eventoPericia?.horaInicio || eventoPericia?.hora || infoPericia?.hora}
                        lugar={(eventoPericia?.descripcion && extraerLugar(eventoPericia.descripcion)) || infoPericia?.lugar}
                        tipoEvento="Pericia Médica Judicial"
                        tramiteId={tramite?.idTramite}
                        className="btn-sm btn-outline-primary rounded-pill me-2"
                    />
                </div>

                {/* Citación al estudio antes de la pericia (V254): el recordatorio usa su hora. */}
                <TarjetaCitacion evento={eventoPericia} tipo="PERICIA" cliente={tramite.cliente} />

            </div>

            {/* Formularios Modales */}
            {mostrarFormularioResultado ? (
                // Formulario para Registrar Pericia Realizada
                <div className="mt-4">
                    <div className="card shadow-sm border-0 rounded-4 p-4">
                        <h5 className="fw-semibold text-primary mb-3">Registrar Pericia Realizada</h5>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">
                                    Fecha de Realización <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={datosResultado.fechaRealizacion}
                                    onChange={e => setDatosResultado({ ...datosResultado, fechaRealizacion: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">Observaciones</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    value={datosResultado.observaciones}
                                    onChange={e => setDatosResultado({ ...datosResultado, observaciones: e.target.value })}
                                    placeholder="Anotaciones sobre la pericia, perito, etc."
                                />
                            </div>
                        </div>
                        <div className="d-flex justify-content-center gap-3 mt-4">
                            <button
                                className="btn btn-success d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                onClick={handleRegistrarPericiaRealizada}
                                disabled={cargando}
                            >
                                <CheckCircleIcon fontSize="small" />
                                {cargando ? 'Guardando...' : 'Confirmar Realización'}
                            </button>
                            <button
                                className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                onClick={() => setMostrarFormularioResultado(false)}
                                disabled={cargando}
                            >
                                <CancelIcon fontSize="small" /> Cancelar
                            </button>
                        </div>
                    </div>
                </div>

            ) : mostrarFormularioReprogramar ? (
                // Formulario para Reprogramar
                <div className="mt-4">
                    <div className="card shadow-sm border-0 rounded-4 p-4">
                        <h5 className="fw-semibold text-warning mb-3">Reprogramar Pericia</h5>
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label fw-semibold">
                                    Motivo de la Reprogramación <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    className="form-control"
                                    rows="2"
                                    value={datosReprogramar.motivo}
                                    onChange={e => setDatosReprogramar({ ...datosReprogramar, motivo: e.target.value })}
                                    placeholder="Ej: El tribunal notificó nueva fecha..."
                                    required
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">
                                    Nueva Fecha <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={datosReprogramar.fecha}
                                    onChange={e => setDatosReprogramar({ ...datosReprogramar, fecha: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">
                                    Nueva Hora <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="time"
                                    className="form-control"
                                    value={datosReprogramar.hora}
                                    onChange={e => setDatosReprogramar({ ...datosReprogramar, hora: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">
                                    Nueva Sede / Lugar <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={datosReprogramar.lugar}
                                    onChange={e => setDatosReprogramar({ ...datosReprogramar, lugar: e.target.value })}
                                    placeholder="Ej: Morgue Judicial, Lavalle 123"
                                    required
                                />
                            </div>
                        </div>
                        <div className="d-flex justify-content-center gap-3 mt-4">
                            <button
                                className="btn btn-warning d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                onClick={handleReprogramarPericia}
                                disabled={cargando}
                            >
                                <EditCalendarIcon fontSize="small" />
                                {cargando ? 'Reprogramando...' : 'Confirmar Reprogramación'}
                            </button>
                            <button
                                className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                onClick={() => setMostrarFormularioReprogramar(false)}
                                disabled={cargando}
                            >
                                <CancelIcon fontSize="small" /> Cancelar
                            </button>
                        </div>
                    </div>
                </div>

                // <-- NUEVO BLOQUE 'ELSE IF' PARA EL MODAL DE CONFIRMACIÓN
            ) : mostrarModalConfirmacionNoAsistio ? (
                renderModalConfirmacionNoAsistio()

            ) : (
                // Botones de Acción Principales
                <div className="d-flex justify-content-center flex-wrap gap-3 mt-4 pt-3 border-top">
                    <button
                        className="btn btn-success d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={() => setMostrarFormularioResultado(true)}
                        disabled={cargando}
                    >
                        <CheckCircleIcon fontSize="small" />
                        Registrar Pericia Realizada
                    </button>
                    <button
                        className="btn btn-warning d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={() => setMostrarFormularioReprogramar(true)}
                        disabled={cargando}
                    >
                        <EditCalendarIcon fontSize="small" />
                        Reprogramar
                    </button>
                    <button
                        className="btn btn-info d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={() => setMostrarModificar(true)}
                        disabled={cargando}
                    >
                        <EditCalendarIcon fontSize="small" />
                        Modificar Turno
                    </button>
                    <button
                        className="btn btn-danger d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={() => setMostrarModalConfirmacionNoAsistio(true)}
                        disabled={cargando}
                    >
                        <EventBusyIcon fontSize="small" />
                        No Asistió
                    </button>
                </div>
            )}

            <ModificarEventoModal
                isOpen={mostrarModificar}
                onClose={() => setMostrarModificar(false)}
                evento={eventoPericia}
                onSave={handleGuardarModificacionEvento}
            />

            {/* Renderizado Condicional de Formularios */}
            {/* {mostrarFormularioResultado && renderFormularioResultado()}
            {mostrarFormularioReprogramar && renderFormularioReprogramar()}
            {mostrarModalConfirmacionNoAsistio && renderModalConfirmacionNoAsistio()} */}
        </div>
    );
}

export default GestorEstadoPericiaAsignada;

